import entityManager from './entityManager';
import { componentRegistry } from './ComponentRegistry';
import type { ArchetypeDefinition } from './ArchetypeLoader';
import Archetype from '../components/Archetype';
import archetypesData from '../../content/dist/archetypes.json';

export default class EntityFactory {
  private archetypes: Record<string, ArchetypeDefinition>;

  constructor(archetypes?: Record<string, ArchetypeDefinition>) {
    // Load from compiled JSON if not provided
    this.archetypes = archetypes ?? archetypesData.archetypes;
  }

  /**
   * Add or override archetypes (useful for testing)
   */
  addArchetypes(archetypes: Record<string, ArchetypeDefinition>): void {
    this.archetypes = { ...this.archetypes, ...archetypes };
  }

  create(archetypeName: string, overrides: Record<string, any> = {}) {
    const archetype = this.archetypes[archetypeName];
    if (!archetype) {
      throw new Error(`[EntityFactory] Archetype "${archetypeName}" not found.`);
    }

    if (archetype.fixedBearing !== undefined && overrides.hexPosition) {
      overrides.hexPosition.bearing = archetype.fixedBearing;
    }

    const entity = entityManager.createEntity();
    const overrideKeys = Object.keys(overrides);

    // Add components from archetype definition, skipping any that will be overridden.
    archetype.components.forEach(componentDef => {
      const { type, ...namedParams } = componentDef;

      const ComponentClass = componentRegistry.get(type)

      if (!ComponentClass) {
        throw new Error(
          `[EntityFactory] Unknown component type "${type}" in archetype "${archetypeName}". ` +
          `Available components: ${componentRegistry.getNames().join(', ')}`
        );
      }

      const key = componentRegistry.getKey(ComponentClass);
      if (!overrideKeys.includes(key)) {
        // Instantiate component with named parameters (empty object for tag components)
        entity.addComponent(ComponentClass, new ComponentClass(namedParams));
      }
    });

    // Apply overrides. The value is an object with named parameters.
    for (const key in overrides) {
      const ComponentClass = componentRegistry.get(key);
      if (!ComponentClass) {
        console.warn(
          `[EntityFactory] Unknown component key in overrides: ${key}. ` +
          `Available components: ${componentRegistry.getNames().join(', ')}`
        );
        continue;
      }
      const params = overrides[key];
      entity.addComponent(ComponentClass, new ComponentClass(params));
    }

    // Add the Archetype component itself.
    entity.addComponent(Archetype, new Archetype({
      name: archetypeName,
      displayName: archetype.displayName,
      overrides,
    }));

    return entity;
  }
}
